import { createContext, useContext, useState, forwardRef, PropsWithChildren, useEffect } from 'react'
import cx from 'classnames'
import Styles from './CollabItem.module.scss'
import { CollabItemProps } from './Types'
import Foco from 'react-foco'
import { useObjectRef } from 'utils/useObjectRef'
import { REPLY_FORM_TRANSITION_DURATION } from './ReplyForm/ReplyForm'

interface ItemId {
  type: string
  id: number | string
}

const CollabItemContext = createContext<{
  isActive: boolean
  isHighlighted: boolean
  editing?: ItemId
  setEditing: (itemId?: ItemId) => void
  isReplying?: boolean
  setIsReplying: (replying: boolean) => void
}>({
  isActive: false,
  isHighlighted: false,
  setEditing: () => {},
  setIsReplying: () => {},
})

export const useCollabItemContext = () => {
  const context = useContext(CollabItemContext)
  if (!context) {
    throw new Error('useCollabItemContext must be wrapped inside a CollabItemContext')
  }
  return context
}
const CollabItem = forwardRef<HTMLDivElement, PropsWithChildren<CollabItemProps>>(
  ({ children, isActive = false, isHighlighted = false, onMouseOver, onMouseLeave, onClick, onClickOutside }, fRef) => {
    const [editing, setEditing] = useState<ItemId>()
    const [isReplying, setIsReplying] = useState(false)
    const ref = useObjectRef(fRef)

    /* -------------------------------------------------------------------------------------------------
     * Autoscroll and selection effects
     * -----------------------------------------------------------------------------------------------*/

    useEffect(() => {
      if (ref.current && isActive) {
        const timeout = setTimeout(() => {
          ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
        }, REPLY_FORM_TRANSITION_DURATION)

        return () => clearTimeout(timeout)
      }
    }, [isActive]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Foco onClickOutside={onClickOutside}>
        <div
          className={cx(Styles.root, {
            [Styles.active]: isActive,
            [Styles.editing]: editing,
            [Styles.highlighted]: isHighlighted,
          })}
          onClick={onClick}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          ref={ref}
        >
          <CollabItemContext.Provider
            value={{
              isActive,
              isHighlighted,
              editing,
              setEditing,
              isReplying,
              setIsReplying,
            }}
          >
            {children}
          </CollabItemContext.Provider>
        </div>
      </Foco>
    )
  },
)

export { CollabItem }
